import GivenEndorsementModalContent from '@company/components/CommunicationHub/Modals/GivenEndorsementModalContent/GivenEndorsementModalContent';
import GivenReviewModalContent from '@company/components/CommunicationHub/Modals/GivenReviewModalContent/GivenReviewModalContent';
import ReceivedReviewModalContent from '@company/components/CommunicationHub/Modals/ReceivedReviewModalContent/ReceivedReviewModalContent';
import ReviewRequestModalContent from '@company/components/CommunicationHub/Modals/ReviewRequestModalContent/ReviewRequestModalContent';
import DirectMessages from '@company/components/CommunicationHub/Tabs/DirectMessages/DirectMessages';
import JobMessages from '@company/components/CommunicationHub/Tabs/JobMessages/JobMessages';
import NewsTab from '@company/components/CommunicationHub/Tabs/News/News';
import ReceivedReviews from '@company/components/CommunicationHub/Tabs/ReceivedReviews/ReceivedReviews';
import ReviewRequests from '@company/components/CommunicationHub/Tabs/ReviewRequests/ReviewRequests';
import ReviewsAndEndorsementsLeft from '@company/components/CommunicationHub/Tabs/ReviewsAndEndoresementsLeft/ReviewsAndEndoresementsLeft';
import { WorkerModal } from '@company/components/WorkerModal';
import { clearCompanyIndicatorsCache } from '@company/hooks/useCompanyIndicators';
import { dismissItemsFromNew } from '@company/services/com-hub/api';
import { useChatContext } from '@company/state/chatContext';
import { useCompanyContext } from '@company/state/companyContext';
import { useDirectMessageContext } from '@company/state/directMessageContext';
import {
  ComHubTabKeys,
  ComHubTabRoutes,
  ComHubTabRoutesType,
  ComHubTabs,
  CounterType,
  GivenReview,
  Review,
  ReviewMetadata,
  ReviewTypeEnum,
  UserActionAssociatedTypeEnum,
  WorkerModalParams,
} from '@company/types/comm-hub.type';
import DirectMessageChat from '@components/Chat/DirectMessageChat';
import JobMessageChat from '@components/Chat/JobMessageChat';
import { SideEndModal } from '@components/elements';
import useAlert from '@hooks/useAlert';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  dismissItems,
  getTabQueryParameter,
  handleDismissOptimisticRollback,
  handleDismissOptimisticUpdate,
  handleRemoveFromListOnlyOptimisticUpdate,
  invalidateQueriesForDismissedItems,
} from '@utils/ComHubUtils';
import { BASE_PATH } from '@utils/Environment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Nav, Navbar, Tab } from 'react-bootstrap';
import { isMobile } from 'react-device-detect';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import './styles.css';

function CommunicationHub() {
  const [showWorkerProfile, setShowWorkerProfile] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [workerProfileId, setWorkerProfileId] = useState<number | null>(null);
  const [workerProfileJobAssignmentId, setWorkerProfileJobAssignmentId] =
    useState<number | null>(null);
  const [workerProfileJobId, setWorkerProfileJobId] = useState<number | null>(
    null
  );
  const [selectedReview, setSelectedReview] = useState<ReviewMetadata | null>(
    null
  );
  const [selectedJobAssignmentId, setSelectedJobAssignmentId] = useState<
    number | null
  >();
  const [selectedEndorsementCaseId, setSelectedEndorsementCaseId] = useState<
    number | null
  >();
  const [reviewType, setReviewType] = useState<ReviewTypeEnum | null>();
  const [activeTabName, setActiveTabName] = useState<string>();
  const [isNavExpanded, setIsNavExpanded] = useState(false);
  const queryClient = useQueryClient();
  const { showSuccessAlert, showErrorAlert, alertMessage } = useAlert();
  const navigate = useNavigate();
  const { activeTab } = useParams();
  const [searchParams] = useSearchParams();
  const { chatPerson, clearChatData, showPrivateInfo, originalJobMessage } =
    useChatContext();
  const {
    worker: directMessagePerson,
    chatId: directMessageChatId,
    clearDirectMessageData,
    originalDirectMessage,
  } = useDirectMessageContext();
  const { indicators, updateNewCommunicationsCount } = useCompanyContext();

  // Local counters state - initialized from indicators, updated after actions
  const [counters, setCounters] = useState<Record<CounterType, number | null>>({
    [CounterType.JOB_CHATS]: null,
    [CounterType.DIRECT_MESSAGES]: null,
    [CounterType.RECEIVED_REVIEWS]: null,
    [CounterType.REVIEW_REQUESTS]: null,
  });

  // Ref to track latest counters without causing callback re-creation
  const countersRef = useRef(counters);
  useEffect(() => {
    countersRef.current = counters;
  }, [counters]);

  // Initialize counters from indicators when they load
  useEffect(() => {
    if (indicators) {
      setCounters((prev) => ({
        [CounterType.JOB_CHATS]:
          prev[CounterType.JOB_CHATS] ??
          indicators.unreadConversationsCount ??
          0,
        [CounterType.DIRECT_MESSAGES]:
          prev[CounterType.DIRECT_MESSAGES] ??
          indicators.unreadDirectChats ??
          0,
        [CounterType.RECEIVED_REVIEWS]:
          prev[CounterType.RECEIVED_REVIEWS] ?? indicators.reviewsCount ?? 0,
        [CounterType.REVIEW_REQUESTS]:
          prev[CounterType.REVIEW_REQUESTS] ??
          indicators.reviewRequestsCount ??
          0,
      }));
    }
  }, [indicators]);

  // Callback to manually update a specific counter after an action (no API call)
  const updateCounter = useCallback(
    (type: CounterType, delta: number) => {
      const currentCount = countersRef.current[type] ?? 0;
      const newCount = Math.max(0, currentCount + delta);
      const actualDelta = newCount - currentCount;

      if (actualDelta !== 0) {
        updateNewCommunicationsCount(actualDelta);
      }

      setCounters((prev) => ({
        ...prev,
        [type]: newCount,
      }));
    },
    [updateNewCommunicationsCount]
  );

  // Callback to reset a specific counter to 0 (for "mark all as read" actions)
  const resetCounter = useCallback(
    (type: CounterType) => {
      setCounters((prev) => {
        const currentValue = prev[type] ?? 0;
        // Update header badge by the negative of current value
        updateNewCommunicationsCount(-currentValue);
        return {
          ...prev,
          [type]: 0,
        };
      });
    },
    [updateNewCommunicationsCount]
  );

  useEffect(() => {
    if (activeTab) {
      setActiveTabName(activeTab);
    } else {
      navigate(ComHubTabKeys.NEW);
    }
  }, [activeTab, navigate]);

  // Handle URL query parameter changes for tab switching
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      // Check if tabParam is a valid tab key from our enum
      const isValidTab = Object.values(ComHubTabKeys).includes(
        tabParam as ComHubTabKeys
      );
      if (isValidTab) {
        setActiveTabName(tabParam);
      } else {
        setActiveTabName(ComHubTabKeys.NEW);
      }
    }
  }, [searchParams]);

  /**
   * useMutation to dismiss items from new from modal interactions
   * this is not duplicated with the mutation function in table components
   * because the modal is not a table component
   */
  const { mutateAsync: dismissMutateAsync } = useMutation({
    mutationFn: dismissItemsFromNew,
    onSuccess: (_, { items }) => {
      handleDismissOptimisticUpdate(queryClient, items, 'removeItems');
      setSelectedReview(null);
      clearCompanyIndicatorsCache();
    },
    onError: (_, { items }) => {
      handleDismissOptimisticRollback(queryClient, items, 'removeItems');
    },
    onSettled: (_, __, { items }) => {
      invalidateQueriesForDismissedItems(queryClient, items);
    },
  });

  const handleTabChange = (tabName: string, onlyNew = false) => {
    const route = Object.values(ComHubTabRoutes).find(
      (comHubRoute: ComHubTabRoutesType) => comHubRoute.key === tabName
    );
    if (route) {
      const baseUrl = `${BASE_PATH}inbox-reviews/${route.key}`;
      const queryParams = new URLSearchParams();
      if (onlyNew) {
        const paramToUse = getTabQueryParameter(tabName);
        queryParams.append(paramToUse, 'true');
      }

      const url = `${baseUrl}?${queryParams}`;
      navigate(url); // Using the mapped key for navigation
      setActiveTabName(route.key);
    }
  };

  const seeReceivedReview = (review: Review) => {
    setSelectedReview(review);
    setReviewType(ReviewTypeEnum.RECEIVED_REVIEW);
    setShowModal(true);
  };

  const handleReplyReviewSuccess = async () => {
    await dismissItems(
      [selectedReview!],
      UserActionAssociatedTypeEnum.COMPANY_RATING,
      dismissMutateAsync
    );
    showSuccessAlert('Reply submitted successfully');
  };

  const seeReviewRequest = (review: ReviewMetadata) => {
    setSelectedReview(review);
    setReviewType(ReviewTypeEnum.REVIEW_REQUEST);
    setShowModal(true);
  };

  const seeGivenReview = (review: GivenReview) => {
    setSelectedJobAssignmentId(review.jobAssignmentId);
    setReviewType(ReviewTypeEnum.GIVEN_REVIEW);
    setShowModal(true);
  };

  const seeGivenEndorsement = (endorsementCaseId: number) => {
    setSelectedEndorsementCaseId(endorsementCaseId);
    setReviewType(ReviewTypeEnum.GIVEN_ENDORSEMENT);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedEndorsementCaseId(null);
    setSelectedJobAssignmentId(null);
    setSelectedReview(null);
    setReviewType(null);
  };

  const handleReviewSuccess = async () => {
    await dismissItems(
      [selectedReview!],
      UserActionAssociatedTypeEnum.RATING_REQUEST,
      dismissMutateAsync
    );
    showSuccessAlert('Review submitted successfully');
    closeModal();
  };

  const handleCloseWorkerProfile = () => {
    setShowWorkerProfile(false);
    setWorkerProfileId(null);
    setWorkerProfileJobAssignmentId(null);
    setWorkerProfileJobId(null);
  };

  const handleShowWorkerProfile = (data: WorkerModalParams) => {
    setWorkerProfileId(data.workerId);
    setWorkerProfileJobAssignmentId(data.jobAssignmentId || null);
    setWorkerProfileJobId(data.jobId || null);
    setShowWorkerProfile(true);
  };

  const showAlertMessage = async (success: boolean, message: string) => {
    if (success) {
      showSuccessAlert(message);
    } else {
      showErrorAlert(message);
    }
  };

  const tabLabel = useMemo(() => {
    return activeTabName
      ? Object.values(ComHubTabRoutes).find(
          (route) => route.key === activeTabName
        )!.label
      : null;
  }, [activeTabName]);

  const getCounterNumber = (tabName: ComHubTabs): number => {
    // Use local counters state (initialized from indicators, updated after actions)
    const jobMessagesCount = counters[CounterType.JOB_CHATS] ?? 0;
    const directMessagesCount = counters[CounterType.DIRECT_MESSAGES] ?? 0;
    const receivedReviewsCount = counters[CounterType.RECEIVED_REVIEWS] ?? 0;
    const reviewRequestsCount = counters[CounterType.REVIEW_REQUESTS] ?? 0;

    switch (tabName) {
      case ComHubTabs.NEW:
        return (
          receivedReviewsCount +
          reviewRequestsCount +
          directMessagesCount +
          jobMessagesCount
        );
      case ComHubTabs.DIRECT_MESSAGES:
        return directMessagesCount;
      case ComHubTabs.JOB_MESSAGES:
        return jobMessagesCount;
      case ComHubTabs.RECEIVED_REVIEWS:
        return receivedReviewsCount;
      case ComHubTabs.RECEIVED_REQUESTS:
        return reviewRequestsCount;
      default:
        return 0;
    }
  };

  const handleCloseDirectMessageChat = async () => {
    if (originalDirectMessage) {
      const items = [
        {
          associatedType: UserActionAssociatedTypeEnum.DIRECT_CHAT,
          associatedIds: [originalDirectMessage.chatId],
        },
      ];

      const searchUnread = searchParams.get('searchUnread') === 'true';
      if (searchUnread) {
        handleRemoveFromListOnlyOptimisticUpdate(queryClient, items);
      }
      invalidateQueriesForDismissedItems(queryClient, items);
    }
    clearDirectMessageData();
  };

  const handleCloseJobChat = async () => {
    if (originalJobMessage) {
      const items = [
        {
          associatedType: UserActionAssociatedTypeEnum.JOB_CHAT,
          associatedIds: [originalJobMessage.chatId],
        },
      ];
      const searchUnread = searchParams.get('searchUnread') === 'true';
      if (searchUnread) {
        handleRemoveFromListOnlyOptimisticUpdate(queryClient, items);
      }
      invalidateQueriesForDismissedItems(queryClient, items);
    }
    clearChatData();
  };

  return (
    <div className="inbox-reviews">
      <Tab.Container
        activeKey={activeTabName}
        onSelect={(tabName) => handleTabChange(tabName!)}
        mountOnEnter
        unmountOnExit
      >
        {isMobile ? (
          <Navbar
            expand="lg"
            expanded={isNavExpanded}
            className="p-3 pb-0"
            onToggle={(expanded) => setIsNavExpanded(expanded)}
            onSelect={(tabName) => {
              handleTabChange(tabName!);
              setIsNavExpanded(false);
            }}
          >
            <Navbar.Brand
              className="text-capitalize fs-6 fw-bold flex-grow-1"
              onClick={() => setIsNavExpanded((prevState) => !prevState)}
            >
              {tabLabel}{' '}
              {activeTab !==
                ComHubTabRoutes[ComHubTabs.REVIEWS_AND_ENDORSEMENTS].key &&
                `(${getCounterNumber(tabLabel!)})`}
            </Navbar.Brand>
            <Navbar.Toggle>
              {isNavExpanded ? (
                <FaChevronUp className="nav-toggle-icon" />
              ) : (
                <FaChevronDown className="nav-toggle-icon" />
              )}
            </Navbar.Toggle>
            <Navbar.Collapse>
              <Nav className="me-auto">
                {Object.entries(ComHubTabRoutes).map(([key, value]) => (
                  <Nav.Item key={key}>
                    <Nav.Link
                      eventKey={value.key}
                      href={`${BASE_PATH}inbox-reviews${value.key}`}
                      onClick={(event) => event.preventDefault()}
                    >
                      {value.label}{' '}
                      {getCounterNumber(value.label) !== undefined
                        ? `(${getCounterNumber(value.label)})`
                        : null}
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
            </Navbar.Collapse>
          </Navbar>
        ) : (
          <Nav variant="tabs" className="d-flex">
            {Object.entries(ComHubTabRoutes).map(([key, value]) => (
              <Nav.Item key={key} className="flex-grow-1 text-center">
                <Nav.Link
                  eventKey={value.key}
                  href={`${BASE_PATH}inbox-reviews${value.key}`}
                  onClick={(event) => event.preventDefault()}
                >
                  {value.label}{' '}
                  {getCounterNumber(value.label)
                    ? `(${getCounterNumber(value.label)})`
                    : null}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        )}

        <Tab.Content className={`p-3 ${isMobile ? 'pt-1' : ''}`}>
          <Tab.Pane eventKey={ComHubTabKeys.NEW}>
            <NewsTab
              onSeeReceivedReview={seeReceivedReview}
              onSeeReviewRequest={seeReviewRequest}
              onShowWorkerProfile={handleShowWorkerProfile}
              jobChatsCount={counters[CounterType.JOB_CHATS] ?? undefined}
              directMessagesCount={
                counters[CounterType.DIRECT_MESSAGES] ?? undefined
              }
              onCounterUpdate={updateCounter}
            />
          </Tab.Pane>
          <Tab.Pane eventKey={ComHubTabKeys.JOB_MESSAGES}>
            <JobMessages
              onShowWorkerProfile={handleShowWorkerProfile}
              onShowAlert={showAlertMessage}
              onCounterUpdate={(delta) =>
                updateCounter(CounterType.JOB_CHATS, delta)
              }
              onResetCounter={() => resetCounter(CounterType.JOB_CHATS)}
            />
          </Tab.Pane>
          <Tab.Pane eventKey={ComHubTabKeys.DIRECT_MESSAGES}>
            <DirectMessages
              onShowWorkerProfile={(workerId) =>
                handleShowWorkerProfile({ workerId })
              }
              onShowAlert={showAlertMessage}
              onCounterUpdate={(delta) =>
                updateCounter(CounterType.DIRECT_MESSAGES, delta)
              }
            />
          </Tab.Pane>
          <Tab.Pane eventKey={ComHubTabKeys.RECEIVED_REVIEWS}>
            <ReceivedReviews
              onShowWorkerProfile={handleShowWorkerProfile}
              onSeeReceivedReview={seeReceivedReview}
              onShowAlert={showAlertMessage}
              newReviewsCount={
                counters[CounterType.RECEIVED_REVIEWS] ?? undefined
              }
              onCounterUpdate={(delta) =>
                updateCounter(CounterType.RECEIVED_REVIEWS, delta)
              }
            />
          </Tab.Pane>
          <Tab.Pane eventKey={ComHubTabKeys.RECEIVED_REQUESTS}>
            <ReviewRequests
              onShowWorkerProfile={handleShowWorkerProfile}
              onSeeReviewRequest={seeReviewRequest}
              onShowAlert={showAlertMessage}
              newRequestsCount={
                counters[CounterType.REVIEW_REQUESTS] ?? undefined
              }
              onCounterUpdate={(delta) =>
                updateCounter(CounterType.REVIEW_REQUESTS, delta)
              }
            />
          </Tab.Pane>
          <Tab.Pane eventKey={ComHubTabKeys.REVIEWS_AND_ENDORSEMENTS}>
            <ReviewsAndEndorsementsLeft
              onShowWorkerProfile={handleShowWorkerProfile}
              onSeeGivenReview={seeGivenReview}
              onSeeGivenEndorsement={seeGivenEndorsement}
              onShowAlert={showAlertMessage}
            />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
      <WorkerModal
        workerId={workerProfileId || undefined}
        jobAssignmentId={workerProfileJobAssignmentId || undefined}
        jobId={workerProfileJobId || undefined}
        show={showWorkerProfile && !!workerProfileId}
        onClose={handleCloseWorkerProfile}
      />

      <SideEndModal
        show={
          showModal &&
          (!!selectedReview ||
            !!selectedJobAssignmentId ||
            !!selectedEndorsementCaseId)
        }
        onHide={closeModal}
        width={
          reviewType === ReviewTypeEnum.GIVEN_ENDORSEMENT ? '40%' : undefined
        }
      >
        {reviewType === ReviewTypeEnum.RECEIVED_REVIEW && selectedReview && (
          <ReceivedReviewModalContent
            selectedReview={selectedReview as Review}
            closeModal={closeModal}
            showAlertMessage={showAlertMessage}
            onReplySuccess={handleReplyReviewSuccess}
            onDismissItems={(reviews) =>
              dismissItems(
                reviews,
                UserActionAssociatedTypeEnum.COMPANY_RATING,
                dismissMutateAsync
              )
            }
          />
        )}
        {reviewType === ReviewTypeEnum.REVIEW_REQUEST && selectedReview && (
          <ReviewRequestModalContent
            selectedReview={selectedReview}
            closeModal={closeModal}
            onReviewSuccess={handleReviewSuccess}
            onDismissItems={(reviews) =>
              dismissItems(
                reviews,
                UserActionAssociatedTypeEnum.RATING_REQUEST,
                dismissMutateAsync
              )
            }
          />
        )}
        {reviewType === ReviewTypeEnum.GIVEN_REVIEW && (
          <GivenReviewModalContent
            jobAssignmentId={selectedJobAssignmentId!}
            closeModal={closeModal}
          />
        )}
        {reviewType === ReviewTypeEnum.GIVEN_ENDORSEMENT && (
          <GivenEndorsementModalContent
            endorsementCaseId={selectedEndorsementCaseId!}
            closeModal={closeModal}
          />
        )}
      </SideEndModal>
      {directMessagePerson && !showWorkerProfile && (
        <SideEndModal
          show={!!directMessagePerson}
          width={isMobile ? '90%' : '50%'}
          onHide={handleCloseDirectMessageChat}
        >
          <DirectMessageChat
            worker={directMessagePerson}
            chatId={directMessageChatId || undefined}
            onClose={handleCloseDirectMessageChat}
          />
        </SideEndModal>
      )}
      {chatPerson && (
        <SideEndModal
          show={!!chatPerson}
          width={isMobile ? '90%' : '50%'}
          onHide={handleCloseJobChat}
        >
          <JobMessageChat
            worker={chatPerson}
            showWorkerInfo={showPrivateInfo}
            onClose={handleCloseJobChat}
          />
        </SideEndModal>
      )}
      <Alert
        show={alertMessage.show}
        variant={alertMessage.variant}
        className="alert-fixed"
        style={{ width: '20rem' }}
      >
        <Alert.Heading>{alertMessage.message}</Alert.Heading>
      </Alert>
    </div>
  );
}

export default CommunicationHub;
